import type { BrandSpecials } from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { Flex, Typography } from '@iheartjane/reefer';
import type { LabeledOption, Nullable } from '@iheartjane/types';
import { useMemo } from 'react';

import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import {
  FilterSelector,
  MobileFiltersModal,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { CustomRow } from '@jane/bloom/types/schemas/roots/bloom/customRow';
import {
  MAGIC_ROW,
  getMagicRowLabel,
} from '@jane/bloom/utils/dm/getMagicRowLabel';
import { createCustomRowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';
import type { AlgoliaProduct } from '@jane/search/types';

type CustomRowDetailsProps = {
  brandSpecials: BrandSpecials;
  cart: Cart | undefined;
  currentSort: SortOptions;
  customLanguage: CustomLanguage;
  customRow: CustomRow;
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<AlgoliaProduct>;
  pauseRefetching: () => void;
  pinnedStore: Nullable<StoreViewModel> | undefined;
  preloadedProductIds: number[];
  rowInstance: SmartSort<AlgoliaProduct> | undefined;
  sortOptions: LabeledOption<SortOptions>[];
  specials: StoreSpecials;
  unpauseRefetching: () => void;
};

export function CustomRowDetails({
  brandSpecials,
  cart,
  currentSort,
  customLanguage,
  customRow,
  janeGold,
  menuTopRowInstance,
  pauseRefetching,
  pinnedStore,
  preloadedProductIds,
  rowInstance,
  sortOptions,
  specials,
  unpauseRefetching,
}: CustomRowDetailsProps) {
  const customRowName =
    customRow.row_type === MAGIC_ROW
      ? getMagicRowLabel()
      : customRow.display_name || customRow.row_type;

  const hasResults = useMemo(
    () => (rowInstance?.products.length ?? 0) > 0,
    [rowInstance?.products.length]
  );

  const weightFilter = useMemo(
    () => createCustomRowWeightFilter(customRow.rules),
    [customRow.rules]
  );

  if (!rowInstance) {
    return <></>;
  }

  return (
    <main className="main">
      <Breadcrumbs
        className="custom-row-breadcrumbs"
        visibleOnViewports="all"
        breadcrumbs={[
          {
            title: 'Home',
            url: '/',
          },
          {
            title: customRowName,
          },
        ]}
      />
      <div className="menu-row-heading">
        <div>
          <div className="menu-row-label">
            <Typography color="text-main" as="h2" variant="header-bold">
              {customRowName}
            </Typography>
          </div>
          <Flex alignItems="center" justifyContent="space-between">
            <Typography
              color="inherit"
              variant="body"
              data-testid="product-count"
              className="product-count"
            >
              {formatProductCount(rowInstance.totalProducts)}
            </Typography>
          </Flex>
        </div>
        <MobileFiltersModal
          availableProductsCount={rowInstance.totalProducts}
          sortOptions={sortOptions}
          currentSort={currentSort}
          shouldShowGold={janeGold.shouldShowGold}
          hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
        />
      </div>
      <FilterSelector
        availableProductsCount={rowInstance.totalProducts}
        currentSort={currentSort}
        hideLabeledFilters
        sortOptions={sortOptions}
        shouldShowGold={janeGold.shouldShowGold}
        hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
      />

      {!hasResults && (
        <Flex justifyContent="center" alignItems="center" height="500px">
          <Typography
            color="inherit"
            variant="title-bold"
            data-testid="no-results"
          >
            No Products Available
          </Typography>
        </Flex>
      )}

      {menuTopRowInstance && (
        <DMMenuRow
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={brandSpecials}
          menuTopRow={menuTopRowInstance}
          cart={cart}
          customLanguage={customLanguage}
          specials={specials}
          janeGold={janeGold}
          pinnedStore={pinnedStore}
          embedInProductGrid={true}
        />
      )}

      <ProductGrid
        brandSpecials={brandSpecials}
        cart={cart}
        customLanguage={customLanguage}
        key={customRow.display_name}
        preloadedProductIds={preloadedProductIds}
        rowInstance={rowInstance}
        specials={specials}
        janeGold={janeGold}
        pinnedStore={pinnedStore}
        weightFilter={weightFilter}
      />
    </main>
  );
}
